import { useState } from 'react'
import { useGetApiCompaniesCurrentEmployees } from '@/api/generated/company-management-user/company-management-user'
import { useGetApiWalletsWalletIdTransactions } from '@/api/generated/user/user'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { WalletTransactionFilters } from './WalletTransactionFilters'
import { WalletTransactionsDataTable } from './WalletTransactionsDataTable'

const DEFAULT_PAGE_SIZE = 10

interface Props {
  walletId: string
  /** Wallet-Liste lädt noch → nur relevant solange `walletId` leer ist. */
  walletResolving?: boolean
  /** Wallet-Liste konnte nicht geladen werden → nur relevant solange `walletId` leer ist. */
  walletResolveError?: boolean
}

/**
 * Smart container for the wallet transactions: holds filter/pagination state, fetches the
 * employee list (filter dropdown) and the transactions, and feeds the dumb
 * `WalletTransactionsDataTable`. Mirrors the `EmployeesTable` container pattern.
 */
export function WalletTransactionsTable({ walletId, walletResolving, walletResolveError }: Props) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [employeeId, setEmployeeId] = useState('')
  const [dateFrom, setDateFrom] = useState<string | undefined>()
  const [dateTo, setDateTo] = useState<string | undefined>()

  const { data: employeesData } = useGetApiCompaniesCurrentEmployees()
  const employees = ((employeesData as any)?.data ?? employeesData ?? []) as {
    id: string | null
    firstName: string | null
    lastName: string | null
  }[]

  // Backend-Falle: offset/limit und startDateTime schließen sich gegenseitig aus
  // (sonst 400). Zeitraum aktiv → Datumsmodus ohne Paginierung, sonst Paginierung.
  // userId kombiniert mit beiden; bei "Alle Mitarbeiter" (leer) weglassen.
  const dateMode = !!dateFrom
  const txParams = dateMode
    ? {
        startDateTime: dateFrom,
        ...(dateTo ? { endDateTime: dateTo } : {}),
        ...(employeeId ? { userId: employeeId } : {}),
      }
    : {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        ...(employeeId ? { userId: employeeId } : {}),
      }

  const { data: txData, isLoading: txLoading, isError: txError } = useGetApiWalletsWalletIdTransactions(
    walletId,
    txParams,
    { query: { enabled: !!walletId } },
  )
  const txRaw = (txData as any)?.data ?? txData
  const transactions = txRaw?.items ?? []
  const totalCount: number = txRaw?.totalCount ?? 0

  // Without a walletId the transactions query is disabled, so fall back to the wallet-list
  // resolution state — otherwise a failed wallet load would look like "no results".
  const isLoading = txLoading || (!walletId && !!walletResolving)
  const isError = txError || (!walletId && !!walletResolveError)

  const handleEmployeeChange = (id: string) => {
    setEmployeeId(id)
    setPage(1)
  }

  const handleDateChange = ({ from, to }: { from?: string; to?: string }) => {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
  }

  return (
    <>
      <WalletTransactionFilters
        employees={employees}
        selectedEmployeeId={employeeId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onEmployeeChange={handleEmployeeChange}
        onDateChange={handleDateChange}
      />

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom p-0 relative">
          <WalletTransactionsDataTable
            isLoading={isLoading}
            isError={isError}
            transactions={transactions}
          />
        </div>

        {!dateMode && (
          <PaginationFooter
            offset={(page - 1) * pageSize}
            limit={pageSize}
            totalCount={totalCount}
            onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
          />
        )}
      </TableCard>
    </>
  )
}
